import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

const SEGMENTS = 48;
const TEXTURE_SIZE = 256;
/**
 * Half a metre of clear ground between the fighter and its own fire. Cosmetic only — the burn
 * reaches everything inside the circle, and a ring never hurts its caster anyway — but fire
 * licking at the feet of the figure raising it reads as a mistake.
 */
export const BURN_CLEARANCE = 0.5;
/** How far the fire is lifted off the floor, so it does not fight the ground for the same pixels. */
const LIFT = 0.03;
/** Two layers turning against each other: what makes the fill churn rather than spin. */
const LAYERS = [
  { seed: 0, spin: 0.55, lift: 0 },
  { seed: 3.1, spin: -0.8, lift: 0.008 },
];

/**
 * The ring of cold fire a ghost raises when it attacks: flat discs lying on the floor, lit by
 * themselves, with the fire painted into a texture rather than built out of geometry. Two of them,
 * turning opposite ways, so the fill churns instead of spinning like a wheel.
 *
 * It is drawn at the fighter's own reach, so what a player sees is exactly what burns.
 */
export class BurnRing {
  private readonly discs: Mesh[] = [];
  private readonly materials: StandardMaterial[] = [];
  private readonly textures: DynamicTexture[] = [];
  private clock = 0;

  constructor(scene: Scene, parent: TransformNode, radius: number, inner: number) {
    const hole = ringHole(radius, inner);
    for (const [i, layer] of LAYERS.entries()) {
      const texture = new DynamicTexture(
        `burnRingPlasma${i}`,
        { width: TEXTURE_SIZE, height: TEXTURE_SIZE },
        scene,
        false,
      );
      paintPlasma(texture, hole, layer.seed);
      texture.hasAlpha = true;

      const material = new StandardMaterial(`burnRingMaterial${i}`, scene);
      // Lit by itself: fire that dimmed in shadow would look like a decal.
      material.disableLighting = true;
      material.emissiveTexture = texture;
      // White, because the colour is in the texture. An emissive colour does not reliably tint an
      // emissive texture on the unlit path, and a ring that came out white however blue the
      // material said it was is what that cost.
      material.emissiveColor = Color3.White();
      material.diffuseColor = Color3.Black();
      material.specularColor = Color3.Black();
      material.opacityTexture = texture;
      material.backFaceCulling = false;

      const disc = MeshBuilder.CreateDisc('burnRing', { radius, tessellation: SEGMENTS }, scene);
      disc.material = material;
      disc.parent = parent;
      // A disc is built standing up; lay it on the floor at the fighter's feet.
      disc.rotation.x = Math.PI / 2;
      disc.position.y = LIFT + layer.lift;
      disc.isPickable = false;
      disc.setEnabled(false);
      this.discs.push(disc);
      this.materials.push(material);
      this.textures.push(texture);
    }
  }

  /** Shows or hides the ring and keeps the fire moving while it is up. */
  update(burning: boolean, dt: number): void {
    if (this.discs[0]!.isEnabled() !== burning)
      for (const disc of this.discs) disc.setEnabled(burning);
    if (!burning) return;
    this.clock += dt;
    LAYERS.forEach((layer, i) => {
      this.discs[i]!.rotation.y = this.clock * layer.spin;
      // The two layers breathe out of step, so the fill never reads as one flat sheet.
      // Kept well under half each: two layers lie on top of one another, and a ring a player
      // cannot see the ground through stops reading as fire.
      this.materials[i]!.alpha = 0.42 + 0.14 * Math.sin(this.clock * (5 + i * 2.7) + i);
    });
  }

  dispose(): void {
    for (const texture of this.textures) texture.dispose();
    for (const material of this.materials) material.dispose();
    for (const disc of this.discs) disc.dispose();
  }
}

/** Cheap moving-looking fire: interfering waves in the angle and the radius, banded blue to white. */
function plasma(angle: number, r: number, seed: number): number {
  const a =
    Math.sin(angle * 5 + r * 9 + seed) +
    Math.sin(angle * 8 - r * 14 + seed * 1.7) +
    Math.sin(r * 21 - seed) +
    Math.sin(angle * 3 + seed * 2.3);
  return (a + 4) / 8;
}

/**
 * One pixel of the fire, at `r` from the middle (1 at the disc's edge) and `angle` around it.
 *
 * Colour in the channels, shape in the alpha, and keeping the two apart is the whole trick. While
 * the transparency was read from the brightness, the hottest pixels were also the most solid, two
 * layers lay on top of one another, and the ring came out a pale plate whatever colour it had been
 * painted. Blue throughout, whitening only where it is hottest.
 */
export function ringPixel(
  r: number,
  angle: number,
  hole: number,
  seed: number,
): { r: number; g: number; b: number; a: number } {
  if (r < hole || r > 1) return { r: 0, g: 0, b: 0, a: 0 };
  // Fade in off the hole's edge and out at the rim, so neither reads as a cut line.
  const edge = smoothstep(hole, hole + 0.1, r) * (1 - smoothstep(0.86, 1, r));
  const value = Math.max(0, Math.min(1, edge * (0.35 + 0.75 * plasma(angle, r, seed))));
  return {
    r: Math.round(255 * (0.15 + 0.7 * value * value * value)),
    g: Math.round(255 * (0.45 + 0.45 * value * value)),
    b: 255,
    a: Math.round(255 * value),
  };
}

/**
 * A disc of blue fire with the middle left clear: nothing inside `hole`, then the fill, fading out
 * at the rim so the circle has an edge rather than a cut.
 */
function paintPlasma(texture: DynamicTexture, hole: number, seed: number): void {
  const ctx = texture.getContext() as CanvasRenderingContext2D;
  const image = ctx.createImageData(TEXTURE_SIZE, TEXTURE_SIZE);
  const half = TEXTURE_SIZE / 2;
  for (let y = 0; y < TEXTURE_SIZE; y++) {
    for (let x = 0; x < TEXTURE_SIZE; x++) {
      const dx = (x + 0.5 - half) / half;
      const dy = (y + 0.5 - half) / half;
      const px = ringPixel(Math.hypot(dx, dy), Math.atan2(dy, dx), hole, seed);
      const i = (y * TEXTURE_SIZE + x) * 4;
      image.data[i] = px.r;
      image.data[i + 1] = px.g;
      image.data[i + 2] = px.b;
      image.data[i + 3] = px.a;
    }
  }
  ctx.putImageData(image, 0, 0);
  texture.update(false);
}

/** How much of the disc's radius the clear middle takes, for a fighter of this half-width. */
export function ringHole(radius: number, inner: number): number {
  return Math.min(0.8, Math.max(0.05, inner / radius));
}

function smoothstep(from: number, to: number, at: number): number {
  const t = Math.max(0, Math.min(1, (at - from) / (to - from)));
  return t * t * (3 - 2 * t);
}
